// Admin-only page: select a car and see exactly how it will appear in the
// Google Shopping XML feed and the Facebook Catalog XML feed (in the active
// Facebook format) BEFORE publishing. The XML shown here is byte-identical
// to what the live /feeds/google and /feeds/facebook endpoints emit for the
// same car — same renderer, same envelope.
import { createFileRoute } from "@tanstack/react-router";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { buildCarFeedPreview, type ChecklistItem } from "@/lib/feed-preview.functions";
import {
  getCarFacebookFormatOverridesFn,
  setCarFacebookFormatOverrideFn,
  type CarFacebookFormatOverrides,
} from "@/lib/car-feed-format-overrides.functions";
import {
  FACEBOOK_FEED_FORMATS,
  type FacebookFeedFormat,
} from "@/lib/feed-format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FeedXmlDiffView } from "@/components/admin/FeedXmlDiffView";
import { toast } from "sonner";
import { AlertTriangle, Copy, Download, Eye, ShoppingBag, Share2, Loader2, GitCompare, Info, Save, RotateCcw, CheckCircle2, XCircle, Rocket } from "lucide-react";

export const Route = createFileRoute("/admin/feed-preview")({
  head: () => ({
    meta: [
      { title: "Feed-preview - AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (<AdminOnlyGuard><AdminFeedPreview /></AdminOnlyGuard>),
});

type CarRow = {
  id: string;
  brand: string;
  model: string;
  variant: string | null;
  year: number;
  status: string;
  is_demo: boolean;
};

function AdminFeedPreview() {
  const buildPreview = useServerFn(buildCarFeedPreview);
  const fetchOverrides = useServerFn(getCarFacebookFormatOverridesFn);
  const persistOverride = useServerFn(setCarFacebookFormatOverrideFn);
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [formatOverride, setFormatOverride] = useState<FacebookFeedFormat | "auto">("auto");

  // Load all cars (admin) so the picker can include drafts/sold/demo.
  const { data: cars, isLoading: carsLoading } = useQuery({
    queryKey: ["admin-feed-preview-cars"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cars")
        .select("id, brand, model, variant, year, status, is_demo")
        .order("updated_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as CarRow[];
    },
  });

  // Load saved per-car format overrides ONCE — they're a single tiny JSON
  // blob in site_settings, so we cache aggressively and update locally on save.
  const { data: overrides } = useQuery({
    queryKey: ["car-fb-format-overrides"],
    queryFn: () => fetchOverrides(),
    staleTime: 60_000,
  });

  // When the admin picks a car, restore its saved format (if any). Picking
  // "auto" in the toggle still means "use whatever the server resolves" —
  // which now includes the per-car override transparently.
  useEffect(() => {
    if (!selectedCarId || !overrides) return;
    const saved = overrides[selectedCarId];
    setFormatOverride(saved ?? "auto");
  }, [selectedCarId, overrides]);

  const filteredCars = useMemo(() => {
    if (!cars) return [];
    const q = search.trim().toLowerCase();
    if (!q) return cars;
    return cars.filter((c) =>
      `${c.brand} ${c.model} ${c.variant ?? ""} ${c.year}`.toLowerCase().includes(q),
    );
  }, [cars, search]);

  const previewQuery = useQuery({
    queryKey: ["car-feed-preview", selectedCarId, formatOverride],
    enabled: !!selectedCarId,
    queryFn: () =>
      buildPreview({
        data: {
          carId: selectedCarId!,
          facebookFormat: formatOverride === "auto" ? undefined : formatOverride,
        },
      }),
  });

  const saveOverrideMutation = useMutation({
    mutationFn: (input: { carId: string; format: FacebookFeedFormat | null }) =>
      persistOverride({ data: input }),
    onSuccess: ({ overrides: next }, vars) => {
      queryClient.setQueryData<CarFacebookFormatOverrides>(
        ["car-fb-format-overrides"],
        next,
      );
      // Re-fetch the preview so warnings/format-source reflect the save.
      queryClient.invalidateQueries({
        queryKey: ["car-feed-preview", vars.carId],
      });
      toast.success(
        vars.format === null
          ? "Per-bil format fjernet — bruger nu globalt standardformat"
          : `Format gemt for denne bil: ${vars.format}`,
      );
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Kunne ikke gemme format");
    },
  });

  const savedFormatForCar: FacebookFeedFormat | null = selectedCarId
    ? overrides?.[selectedCarId] ?? null
    : null;

  const activePreviewFormat: FacebookFeedFormat | null =
    previewQuery.data?.facebookFormat ??
    (formatOverride === "auto" ? null : formatOverride);

  const canSaveOverride =
    !!selectedCarId &&
    !!activePreviewFormat &&
    activePreviewFormat !== savedFormatForCar &&
    !saveOverrideMutation.isPending;

  // Publish flips cars.status to "active". Blocked when the checklist has
  // any `critical` item — the button itself is disabled, but we ALSO guard
  // here so a stale render can't bypass the gate.
  const publishMutation = useMutation({
    mutationFn: async (carId: string) => {
      const { error } = await supabase
        .from("cars")
        .update({ status: "active" })
        .eq("id", carId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Bilen er publiceret — status sat til 'active'.");
      queryClient.invalidateQueries({ queryKey: ["car-feed-preview", selectedCarId] });
      queryClient.invalidateQueries({ queryKey: ["admin-feed-preview-cars"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Kunne ikke publicere bilen");
    },
  });


  const copy = (label: string, text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopieret til udklipsholder`);
  };

  const downloadXml = (filename: string, xml: string) => {
    try {
      const blob = new Blob([xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      toast.success(`Downloadet: ${filename}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Kunne ikke downloade fil",
      );
    }
  };

  const slugify = (s: string) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "car";

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground">Feed-preview</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Vælg en bil og se præcis hvordan den vil se ud i Google Shopping og
        Facebook Catalog feeds — før publicering. XML&apos;en er byte-identisk
        med hvad de live feeds udsender.
      </p>

      {/* Picker */}
      <Card className="mt-6 max-w-3xl">
        <CardHeader>
          <CardTitle className="text-lg">Vælg bil</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Søg på mærke / model / variant / årgang…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <Select
              value={selectedCarId ?? ""}
              onValueChange={(v) => setSelectedCarId(v || null)}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    carsLoading
                      ? "Indlæser biler…"
                      : `Vælg bil (${filteredCars.length} tilgængelige)`
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {filteredCars.slice(0, 200).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.brand} {c.model} {c.variant ?? ""} {c.year}
                    {c.status !== "active" ? `  ·  [${c.status}]` : ""}
                    {c.is_demo ? "  ·  [demo]" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={formatOverride}
              onValueChange={(v) => setFormatOverride(v as typeof formatOverride)}
            >
              <SelectTrigger className="min-w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Facebook-format: aktive (auto)</SelectItem>
                {FACEBOOK_FEED_FORMATS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    Facebook-format: {f.shortLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Per-car save controls — only meaningful once a car is picked. */}
          {selectedCarId ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 p-3">
              <span className="text-xs text-muted-foreground">
                Gemt format for denne bil:{" "}
                <code className="text-foreground">
                  {savedFormatForCar ?? "ingen (bruger globalt standard)"}
                </code>
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="default"
                  disabled={!canSaveOverride}
                  onClick={() => {
                    if (!selectedCarId || !activePreviewFormat) return;
                    saveOverrideMutation.mutate({
                      carId: selectedCarId,
                      format: activePreviewFormat,
                    });
                  }}
                  title={
                    activePreviewFormat
                      ? `Gem '${activePreviewFormat}' som standardformat for denne bil`
                      : "Vælg et eksplicit format først"
                  }
                >
                  {saveOverrideMutation.isPending ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-3.5 w-3.5" />
                  )}
                  Gem format for denne bil
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!savedFormatForCar || saveOverrideMutation.isPending}
                  onClick={() => {
                    if (!selectedCarId) return;
                    saveOverrideMutation.mutate({
                      carId: selectedCarId,
                      format: null,
                    });
                  }}
                  title="Fjern det gemte format og fald tilbage til globalt standardformat"
                >
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  Nulstil
                </Button>
              </div>
            </div>
          ) : null}

          {filteredCars.length > 200 ? (
            <p className="text-xs text-muted-foreground">
              Viser de første 200 — søg for at indsnævre.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* Preview */}
      {!selectedCarId ? (
        <div className="mt-8 flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <Eye className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Vælg en bil ovenfor for at se feed-preview.
          </p>
        </div>
      ) : previewQuery.isLoading ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Bygger preview…
        </div>
      ) : previewQuery.isError ? (
        <div className="mt-8 rounded-xl bg-destructive/10 p-4 text-sm text-destructive">
          Kunne ikke bygge preview:{" "}
          {previewQuery.error instanceof Error
            ? previewQuery.error.message
            : String(previewQuery.error)}
        </div>
      ) : previewQuery.data ? (
        <div className="mt-8 space-y-6">
          {/* Header */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{previewQuery.data.carLabel}</CardTitle>
              <p className="text-xs text-muted-foreground">
                Domæne: <code>{previewQuery.data.domain}</code> · Facebook-format:{" "}
                <code>{previewQuery.data.facebookFormat}</code>{" "}
                <span className="ml-1 inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {previewQuery.data.facebookFormatSource === "carOverride"
                    ? "gemt for bilen"
                    : previewQuery.data.facebookFormatSource === "input"
                      ? "preview-toggle"
                      : "globalt standard"}
                </span>
              </p>
            </CardHeader>
            {previewQuery.data.liveUnavailableReason ? (
              <CardContent>
                <div className="flex items-start gap-2 rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Live-diff utilgængelig: {previewQuery.data.liveUnavailableReason}
                  </span>
                </div>
              </CardContent>
            ) : null}
          </Card>

          {/* Two-column: XML previews on the left, validation checklist on the right. */}
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="grid gap-6 xl:grid-cols-2">
              <FeedXmlCard
                icon={<ShoppingBag className="h-5 w-5 text-accent" />}
                title="Google Shopping"
                subtitle="/feeds/google"
                xml={previewQuery.data.googleXml}
                liveXml={previewQuery.data.googleLiveXml}
                feedLabel="Google Shopping"
                onCopy={() => copy("Google XML", previewQuery.data!.googleXml)}
                onDownload={() =>
                  downloadXml(
                    `google-shopping-${slugify(previewQuery.data!.carLabel)}.xml`,
                    previewQuery.data!.googleXml,
                  )
                }
              />
              <FeedXmlCard
                icon={<Share2 className="h-5 w-5 text-accent" />}
                title={`Facebook Catalog (${previewQuery.data.facebookFormat})`}
                subtitle="/feeds/facebook"
                xml={previewQuery.data.facebookXml}
                liveXml={previewQuery.data.facebookLiveXml}
                feedLabel="Facebook Catalog"
                onCopy={() => copy("Facebook XML", previewQuery.data!.facebookXml)}
                onDownload={() =>
                  downloadXml(
                    `facebook-catalog-${previewQuery.data!.facebookFormat}-${slugify(previewQuery.data!.carLabel)}.xml`,
                    previewQuery.data!.facebookXml,
                  )
                }
              />
            </div>

            <ValidationChecklistCard
              checklist={previewQuery.data.checklist}
              canPublish={previewQuery.data.canPublish}
              carStatus={previewQuery.data.carStatus}
              isDemo={previewQuery.data.isDemo}
              isPublishing={publishMutation.isPending}
              onPublish={() => {
                if (!selectedCarId) return;
                if (!previewQuery.data?.canPublish) {
                  toast.error("Ret kritiske fejl før publicering.");
                  return;
                }
                publishMutation.mutate(selectedCarId);
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FeedXmlCard({
  icon,
  title,
  subtitle,
  xml,
  liveXml,
  feedLabel,
  onCopy,
  onDownload,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  xml: string;
  liveXml: string | null;
  feedLabel: string;
  onCopy: () => void;
  onDownload: () => void;
}) {
  const hasLive = !!liveXml;

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            {icon}
            {title}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCopy}>
            <Copy className="mr-2 h-3.5 w-3.5" />
            Kopiér
          </Button>
          <Button variant="outline" size="sm" onClick={onDownload}>
            <Download className="mr-2 h-3.5 w-3.5" />
            Download
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={hasLive ? "diff" : "preview"}>
          <TabsList>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="live" disabled={!hasLive}>
              Live
            </TabsTrigger>
            <TabsTrigger value="diff" disabled={!hasLive}>
              <GitCompare className="mr-1.5 h-3.5 w-3.5" />
              Diff
            </TabsTrigger>
          </TabsList>
          <TabsContent value="preview" className="mt-3">
            <pre className="max-h-[600px] overflow-auto rounded-lg bg-muted p-4 text-xs leading-relaxed text-foreground">
              <code>{xml}</code>
            </pre>
          </TabsContent>
          <TabsContent value="live" className="mt-3">
            {liveXml ? (
              <pre className="max-h-[600px] overflow-auto rounded-lg bg-muted p-4 text-xs leading-relaxed text-foreground">
                <code>{liveXml}</code>
              </pre>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                Ingen live-version af {feedLabel} for denne bil.
              </div>
            )}
          </TabsContent>
          <TabsContent value="diff" className="mt-3">
            <FeedXmlDiffView
              liveXml={liveXml}
              previewXml={xml}
              feedLabel={feedLabel}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

/**
 * Sidebar checklist mirroring the structured publish-readiness rows from the
 * preview server function. Critical rows block the publish button; warnings
 * are surfaced but do not gate publishing.
 */
function ValidationChecklistCard({
  checklist,
  canPublish,
  carStatus,
  isDemo,
  isPublishing,
  onPublish,
}: {
  checklist: ChecklistItem[];
  canPublish: boolean;
  carStatus: string;
  isDemo: boolean;
  isPublishing: boolean;
  onPublish: () => void;
}) {
  const criticalCount = checklist.filter((c) => c.severity === "critical").length;
  const warningCount = checklist.filter((c) => c.severity === "warning").length;
  const okCount = checklist.filter((c) => c.severity === "ok").length;

  // Hide the publish button entirely when the car is already active and not
  // demo — there's nothing to publish.
  const alreadyPublished = carStatus === "active" && !isDemo;

  return (
    <Card className="lg:sticky lg:top-4 self-start">
      <CardHeader>
        <CardTitle className="text-base">Publiceringstjekliste</CardTitle>
        <p className="text-xs text-muted-foreground">
          {criticalCount > 0
            ? `${criticalCount} kritisk(e) fejl blokerer publicering.`
            : warningCount > 0
              ? `Klar til publicering — ${warningCount} advarsel(er) bør gennemgås.`
              : "Alt OK — klar til publicering."}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary chips */}
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          {criticalCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive">
              <XCircle className="h-3 w-3" /> {criticalCount} kritisk
            </span>
          ) : null}
          {warningCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" /> {warningCount} advarsel
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-3 w-3" /> {okCount} OK
          </span>
        </div>

        <ul className="space-y-2">
          {checklist.map((item) => (
            <ChecklistRow key={item.id} item={item} />
          ))}
        </ul>

        <div className="border-t border-border pt-3">
          <Button
            className="w-full"
            disabled={!canPublish || isPublishing || alreadyPublished}
            onClick={onPublish}
            title={
              alreadyPublished
                ? "Bilen er allerede publiceret"
                : !canPublish
                  ? "Ret kritiske fejl før publicering"
                  : "Sæt status til 'active' og publicér til feeds"
            }
          >
            {isPublishing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Rocket className="mr-2 h-4 w-4" />
            )}
            {alreadyPublished
              ? "Allerede publiceret"
              : canPublish
                ? "Publicér bil"
                : "Blokeret af kritiske fejl"}
          </Button>
          {!canPublish && !alreadyPublished ? (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Knappen aktiveres når alle kritiske punkter er løst.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  const Icon =
    item.severity === "critical"
      ? XCircle
      : item.severity === "warning"
        ? AlertTriangle
        : CheckCircle2;
  const tone =
    item.severity === "critical"
      ? "text-destructive"
      : item.severity === "warning"
        ? "text-amber-600 dark:text-amber-400"
        : "text-emerald-600 dark:text-emerald-400";
  return (
    <li className="flex items-start gap-2 text-xs">
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tone}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <span className="font-medium text-foreground">{item.label}</span>
        {item.detail ? (
          <span className="ml-1 text-muted-foreground">— {item.detail}</span>
        ) : null}
      </div>
    </li>
  );
}
